<template>
  <modal @close="onClose" width="70rem">
    <h3 slot="header">{{ $t('health.recipe.new') }}</h3>
    <div slot="body">
      <alert
        v-if="alertVisible"
        :message="alertMessage"
        :type="alertType">
      </alert>
      <form v-on:submit.prevent>
        <grid-row col-spaced>
          <grid-col col-size="1">
            <label-input
              id="nameInput"
              ref="nameInput"
              form-size="m"
              :label="$t('health.recipe.name')"
              v-model="name"
              :margin="false"
              :error="nameError"
              :errorMessage="displayNameErrorMessage">
            </label-input>
          </grid-col>
          <grid-col col-size="1">
            <label-input
              id="nameEnInput"
              ref="nameEnInput"
              form-size="m"
              :label="$t('health.recipe.nameEn')"
              v-model="nameEn"
              :margin="false"
              :error="nameEnError"
              :errorMessage="displayNameEnErrorMessage">
            </label-input>
          </grid-col>
          <grid-col col-size="1">
            <span class="margin-right-m">{{ $t('health.recipe.type') }}</span>
            <v-select
              class="v-select--light"
              :value="selectedType"
              :onChange="changeType"
              :searchable="true"
              :options="$t('health.nutritionalPlan.mealTypes')">
            </v-select>
          </grid-col>
        </grid-row>

        <grid-row class="margin-top-m">
          <grid-col col-size="3">
            <grid-row>
              <grid-col col-size="3">
                <div class="margin-bottom-m">
                  <checkbox
                    id="basic-cook-enabled"
                    :label="$t('health.recipe.basicCooking')"
                    v-on:change="onBasicCookChanged"
                    :checked="basicCook">
                  </checkbox>
                </div>
              </grid-col>
            </grid-row>
            <grid-row>
              <grid-col col-size="1">
                <checkbox
                  id="vegetarian-enabled"
                  :label="$t('health.recipe.vegetarian')"
                  v-on:change="onVegetarianChange"
                  :checked="vegetarian">
                </checkbox>
              </grid-col>
              <grid-col col-size="1">
                <checkbox
                  id="vegan-enabled"
                  :label="$t('health.recipe.vegan')"
                  v-on:change="onVeganChange"
                  :checked="vegan">
                </checkbox>
              </grid-col>
              <grid-col col-size="1">
                <checkbox
                  id="pescetarian-enabled"
                  :label="$t('health.recipe.pescetarian')"
                  v-on:change="onPescetarianChange"
                  :checked="pescetarian">
                </checkbox>
              </grid-col>
            </grid-row>
            <grid-row class="margin-top-m">
              <grid-col col-size="4">
                <multi-select
                  :options="getTagOptions"
                  :selected-options="selectedTags"
                  placeholder="Añadir una propiedad"
                  @select="onSelectTag">
                </multi-select>
              </grid-col>
            </grid-row>
          </grid-col>
        </grid-row>

        <grid-row class="margin-top-m">
          <grid-col col-size="2">
            <p class="center-vertical">
              {{ $t('health.recipe.predictedScore') }}: {{ predictedScore }}
              <span class="dot" :style="{'background-color': predictedScoreColor}"></span>
              {{ $t('health.recipe.predictedIsp') }}: {{ predictedIsp }}
              <span class="dot" :style="{'background-color': predictedIspColor}"></span>
            </p>
          </grid-col>
          <grid-col col-size="2">
            <span
              class="align-right"
              v-tooltip.left.top-start="$t('health.nutritionalPlan.addFood')"
              @click="showFoodModal = true">
              <font-awesome-icon
                class="header-add-button-icon center-vertical"
                :icon="icon.add"/>
            </span>
          </grid-col>
        </grid-row>

        <grid-row class="margin-top-m">
          <grid-col col-size="4">
            <food-table
              ref="foodTable"
              :items="selectedFoods"
              :noWeight="true"
              :showFibre="true"
              v-on:delete="deleteFood"
              v-on:updateAmount="updateFoodAmount"
            />
          </grid-col>
        </grid-row>

        <grid-row class="margin-top-l">
          <grid-col col-size="4">
            <total-macros-table
              ref="totalMacrosTable"
              :items="foodDistributionItems"
              :showFibre="true"
            />
          </grid-col>
        </grid-row>
      </form>

      <add-food-modal
        v-if="showFoodModal"
        v-model="food"
        :foods="getFilteredFoods"
        v-on:close="closeFoodModal"
        v-on:input="addFood"
      />

      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>{{ foodToDelete.food_name }}</li>
          </ul>
        </div>
      </ok-cancel-modal>
    </div>

    <div slot="footer">
      <btn-pill
        :btnText="$t('health.recipe.new')"
        v-on:click="onSave"
        btn-size="m"
        :disabled="!scoreLoaded || selectedFoods.length === 0">
      </btn-pill>
      <btn-pill
        :btnText="$t('commons.cancel')"
        v-on:click="onClose"
        btn-size="m"
        btn-type="secondary">
      </btn-pill>
    </div>
  </modal>
</template>

<script>
import Modal from '@/components/shared/dialogs/Modal';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Checkbox from '@/components/shared/forms/Checkbox';
import api from 'BRAND/api/recipe';
import nutritionApi from 'BRAND/api/nutritionPlan';
import Alert from '@/components/shared/alert/Alert';
import LabelInput from '@/components/shared/forms/LabelInput';
import vSelect from 'vue-select';
import FoodTable from 'BRAND/components/nutritionPlan/FoodTable';
import AddFoodModal from 'BRAND/components/nutritionPlan/AddFoodModal';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import TotalMacrosTable from 'BRAND/components/nutritionPlan/TotalMacrosTable';
import { MultiSelect } from 'vue-search-select';
import { mapGetters } from 'vuex';

export default {
  name: 'RecipeCreateModal',
  components: {
    Modal,
    GridRow,
    GridCol,
    FontAwesomeIcon,
    Alert,
    LabelInput,
    vSelect,
    FoodTable,
    Checkbox,
    AddFoodModal,
    OkCancelModal,
    BtnPill,
    TotalMacrosTable,
    MultiSelect
  },
  data () {
    return {
      icon: { add: faPlusCircle },
      alertVisible: false,
      alertMessage: '',
      alertType: '',
      mealTypes: ['Desayuno', 'Almuerzo', 'Comida', 'Merienda', 'Cena'],
      tagOptions: [],
      showFoodModal: false,
      food: {},
      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      foodToDelete: {},
      name: '',
      nameEn: '',
      type: null,
      selectedType: null,
      foods: [],
      selectedFoods: [],
      basicCook: false,
      vegetarian: false,
      vegan: false,
      pescetarian: false,
      foodDistributionItems: [],
      carbsDistribution: 0,
      selectedTags: [],
      totalProtein: 0,
      totalFat: 0,
      totalCarbs: 0,
      totalFibre: 0,
      predictedScoreColor: '',
      predictedScore: 0,
      predictedIspColor: '',
      predictedIsp: 0,
      nameError: false,
      nameEnError: false,
      displayNameErrorMessage: '',
      displayNameEnErrorMessage: '',
      scoreLoaded: false
    };
  },
  mounted () {
    this.requestFoodData();
    this.requestTags();
  },
  watch: {
    selectedFoods: {
      handler () {
        this.updateMacroTotals();
        this.scoreLoaded = false;
      }
    }
  },
  computed: {
    ...mapGetters(['getLanguageCode']),
    getFilteredFoods () {
      return this.foods
        ? this.foods.filter(food => !this.selectedFoods.some(item => item.food_id === food.food_id))
        : [];
    },
    getTagOptions () {
      return this.tagOptions;
    }
  },
  methods: {
    onClose () {
      // Solo cerrar si no hay modales internos abiertos
      if (this.showFoodModal || this.showConfirmationDeleteModal) return;
      this.$emit('close');
    },
    onSave () {
      this.clearErrors();
      if (this.name === '') {
        this.nameError = true;
        this.displayNameErrorMessage = this.$t('form.required');
      }
      if (this.nameEn === '') {
        this.nameEnError = true;
        this.displayNameEnErrorMessage = this.$t('form.required');
      }
      if (this.nameError || this.nameEnError) return;
      if (this.type === null) {
        showAlert(this, this.$t('health.recipe.errorNoType'), 'error');
        return;
      }
      if (this.selectedFoods.length === 0) {
        showAlert(this, this.$t('health.recipe.recipeHasToHaveItemsError'), 'error');
        return;
      }

      const items = this.selectedFoods.map(item => ({ food_id: item.food_id, amount: item.amount }));
      const tags = this.selectedTags.map(tag => parseInt(tag.value));
      const body = {
        name: this.name,
        name_en: this.nameEn,
        type: this.type,
        items,
        vegetarian: this.vegetarian,
        vegan: this.vegan,
        pescetarian: this.pescetarian,
        basicCook: this.basicCook,
        tags,
        predicted_score_color: this.predictedScore,
      };

      api.addRecipeNutri(this, body)
        .then(() => {
          showAlert(this, this.$t('health.recipe.successCreate'), 'success');
          this.$emit('created');
          this.$emit('close');
        })
        .catch((error) => {
          const msg = error.body.message === 'Recipe must have at least one item.'
            ? this.$t('health.recipe.recipeHasToHaveItemsError')
            : this.$t('health.recipe.errorCreate');
          showAlert(this, msg, 'error');
        });
    },
    clearErrors () {
      this.nameError = false;
      this.nameEnError = false;
      this.displayNameErrorMessage = '';
      this.displayNameEnErrorMessage = '';
    },
    changeType (type) {
      if (type === null) type = this.$t('health.nutritionalPlan.mealTypes')[0];
      const typeIndex = this.$t('health.nutritionalPlan.mealTypes').findIndex(e => e === type);
      if (typeIndex === -1) {
        this.type = this.mealTypes[0];
        this.selectedType = this.$t('health.nutritionalPlan.mealTypes')[0];
      } else {
        this.type = this.mealTypes[typeIndex];
      }
    },
    deleteFood (food) {
      this.foodToDelete = food;
      this.deleteModalMessage = this.$t('health.nutritionalPlan.deleteFood');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      this.showConfirmationDeleteModal = false;
      this.selectedFoods = this.selectedFoods.filter(v => v.food_id !== this.foodToDelete.food_id);
      this.foodToDelete = {};
    },
    onDeleteCancel () {
      this.foodToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    updateFoodAmount (food) {
      const languageCode = this.getLanguageCode;
      nutritionApi.getFoodInfo(this, food.food_id, languageCode).then((data) => {
        const newItem = {
          amount: food.amount,
          food_id: food.food_id,
          food_name: food.food_name,
          calories: Math.round(((data.energy_kJ_value * food.amount) / (4.184 * data.portion)) * 100) / 100,
          protein: Math.round(((data.protein_grams_value * food.amount) / data.portion) * 100) / 100,
          fat: Math.round(((data.fat_grams_value * food.amount) / data.portion) * 100) / 100,
          carbs: Math.round(((data.carbohydrate_gram_value * food.amount) / data.portion) * 100) / 100,
          fibre: Math.round(((data.fibre_gram_value * food.amount) / data.portion) * 100) / 100
        };
        const index = this.selectedFoods.findIndex(e => e.food_id === food.food_id);
        const updated = [...this.selectedFoods];
        updated[index] = newItem;
        this.selectedFoods = updated;
      });
    },
    onBasicCookChanged (value) { this.basicCook = value; },
    onVegetarianChange (value) { this.vegetarian = value; },
    onVeganChange (value) { this.vegan = value; },
    onPescetarianChange (value) { this.pescetarian = value; },
    closeFoodModal () {
      this.showFoodModal = false;
      this.food = {};
    },
    onSelectTag (tags) { this.selectedTags = tags; },
    addFood (food) {
      this.food = food;
      const languageCode = this.getLanguageCode;
      nutritionApi.getFoodInfo(this, food.food_id, languageCode).then((data) => {
        this.selectedFoods.push({
          food_id: food.food_id,
          food_name: food.food_name,
          amount: data.portion,
          calories: Math.round((data.energy_kJ_value / 4.184) * 100) / 100,
          protein: data.protein_grams_value,
          fat: data.fat_grams_value,
          carbs: data.carbohydrate_gram_value,
          fibre: data.fibre_gram_value
        });
      }).catch(() => {
        showAlert(this, this.$t('health.nutritionalPlan.errorAddFood'), 'error');
      });
    },
    requestFoodData () {
      const languageCode = this.getLanguageCode;
      nutritionApi.getFoodList(this, languageCode).then(data => {
        this.foods = data;
      });
    },
    requestTags () {
      const languageCode = this.getLanguageCode;
      api.getTags(this, languageCode).then(data => {
        this.tagOptions = data.items.map(tag => ({
          value: tag.id.toString(),
          text: tag.name
        }));
      });
    },
    updateMacroTotals () {
      this.totalProtein = 0;
      this.totalFat = 0;
      this.totalCarbs = 0;
      this.totalFibre = 0;
      this.selectedFoods.forEach(food => {
        this.totalProtein += food.protein;
        this.totalFat += food.fat;
        this.totalCarbs += food.carbs;
        this.totalFibre += food.fibre;
      });
      this.requestPredictedScore();
      const totalCalories = this.totalCarbs * 4 + this.totalProtein * 4 + this.totalFat * 9;
      this.foodDistributionItems = [
        {
          name: this.$t('health.recipe.totalGrams'),
          proteins: Math.round(this.totalProtein * 100) / 100,
          fats: Math.round(this.totalFat * 100) / 100,
          carbs: Math.round(this.totalCarbs * 100) / 100,
          calories: '-',
          fibre: Math.round(this.totalFibre * 100) / 100
        },
        {
          name: this.$t('health.recipe.totalKcals'),
          proteins: Math.round(this.totalProtein * 4 * 100) / 100,
          fats: Math.round(this.totalFat * 9 * 100) / 100,
          carbs: Math.round(this.totalCarbs * 4 * 100) / 100,
          calories: Math.round(totalCalories * 100) / 100,
          fibre: '-'
        },
        {
          name: this.$t('health.recipe.kcalDistribution'),
          proteins: Math.round((this.totalProtein * 4 / totalCalories) * 100) + '%',
          fats: Math.round((this.totalFat * 9 / totalCalories) * 100) + '%',
          carbs: Math.round((this.totalCarbs * 4 / totalCalories) * 100) + '%',
          calories: '-',
          fibre: '-'
        }
      ];
    },
    requestPredictedScore () {
      api.getPredictedScore(
        this,
        Math.round(this.totalProtein * 100) / 100,
        Math.round(this.totalFat * 100) / 100,
        Math.round(this.totalCarbs * 100) / 100,
        Math.round(this.totalFibre * 100) / 100
      ).then((data) => {
        this.predictedScoreColor = data.color;
        this.predictedScore = Math.round(data.score * 100) / 100;
        this.predictedIsp = Math.round(data.isp * 100) / 100;
        this.predictedIspColor = data.isp < 31 ? 'red' : data.isp <= 60 ? 'yellow' : 'green';
        this.scoreLoaded = true;
      });
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  setTimeout(() => { context.alertVisible = false; }, 3000);
};
</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/variables";

.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
}

.align-right {
  float: right;
  clear: both;
}

.dot {
  height: 25px;
  width: 25px;
  border-radius: 50%;
  display: inline-block;
}

.center-vertical {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/components/nutritionPlan/CreateRecipeModal.vue